import React, { useEffect,useState } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Typography,
  message,
  Table,
  DatePicker,
  Checkbox,
  Upload,
} from "antd";
import { PlusOutlined, DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import useUser from "../../../hooks/useUser";
import useToast from "../../../hooks/userToast";
import { baseURl } from "../../../utils/urls";
import PropTypes from "prop-types";

UploadStudyModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
};
const { Option } = Select;
const { Text } = Typography;

function UploadStudyModal({ onCancel }) {
  const [form] = Form.useForm();
  const { userToken, category, userID } = useUser();
  const { toastError, toastSuccess } = useToast();

  const [radiologists, setRadiologists] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [radiologistMap, setRadiologistMap] = useState({});
  const [priorityMap, setPriorityMap] = useState({});
  const [radiologistDetails, setRadiologistDetails] = useState([]);
  const [currentDetail, setCurrentDetail] = useState({ radiologist: "", priority: "" });
  const [imageFiles, setImageFiles] = useState([]);
  const [studyFile, setStudyFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ageDisplay, setAgeDisplay] = useState();

  useEffect(() => {
    fetchPriorities();
    if (category) fetchRadiologists();
  }, [category]);

  const fetchRadiologists = async () => {
    try {
      const response = await axios.get(
        `${baseURl}/users?role=radiologist&categoryId=${category}&limit=1000`,
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      const data = response?.data?.users?.data || [];
      setRadiologists(data);
      setRadiologistMap(Object.fromEntries(data.map(item => [item.id, item.name])));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPriorities = async () => {
    try {
      const response = await axios.get(`${baseURl}/priority`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      const data = response?.data?.priority?.data || [];
      setPriorities(data);
      setPriorityMap(Object.fromEntries(data.map(item => [item.id, item.name])));
    } catch (err) {
      console.error(err);
    }
  };

 const handleDOBChange = (date) => {
  if (!date) {
    setAgeDisplay("");
    return;
  }

  const now = dayjs();

  if (date.isAfter(now)) {
    toastError("Date of birth cannot be in the future")
    setAgeDisplay("");
    return;
  }

  const diffInMonths = now.diff(date, "month");
  const ageInYears = diffInMonths / 12;

  if (ageInYears < 1) {
    setAgeDisplay(ageInYears.toFixed(1));
  } else {
    setAgeDisplay(Math.round(ageInYears));
  }
};

  const handleAddRow = () => {
    const { radiologist, priority } = currentDetail;
    if (radiologist && priority) {
      setRadiologistDetails(prev => [...prev, { radiologist, priority, category }]);
      setCurrentDetail({ radiologist: "", priority: "" });
    } else {
      message.warning("Please select radiologist and priority.");
    }
  };

  const handleDeleteRow = (index) => {
    setRadiologistDetails(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append("age", ageDisplay);
    formData.append("gender", values.gender);
    formData.append("refer_by", values.reference);
    formData.append("history_description", values.description);
    formData.append("modality", values.modality);
    formData.append("site_person_id", userID);
    formData.append("emergency_flag", values.emergency ? 1 : 0);
    formData.append("radiologistPrefrence", JSON.stringify(radiologistDetails));

    imageFiles.forEach(file => formData.append("image", file));
    if (studyFile) formData.append("files", studyFile);

    try {
      setLoading(true);
      const res = await axios.post(`${baseURl}/orthanc/uploadStudy`, formData, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res?.status === 208) {
        toastError(res.data.error);
      } else {
        toastSuccess("Added Successfully!");
        form.resetFields();
        setRadiologistDetails([]);
        setImageFiles([]);
        setStudyFile(null);
        onCancel();
      }
    } catch (err) {
      toastError(err?.response?.data?.error || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Radiologist",
      dataIndex: "radiologist",
      key: "radiologist",
      render: id => radiologistMap[id] || "N/A",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: id => priorityMap[id] || "N/A",
    },
    {
      title: "Action",
      key: "action",
      render: (_, __, index) => (
        <Button type="link" icon={<DeleteOutlined />} onClick={() => handleDeleteRow(index)}>
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Form layout="vertical" form={form} onFinish={handleSubmit}>
      <Typography.Text strong>Radiologist Details</Typography.Text>
      <div className="row">
        <div className="col-lg-4">
          <Form.Item label="Radiologist">
            <Select
              value={currentDetail.radiologist || undefined}
              onChange={(value) =>
                setCurrentDetail(prev => ({ ...prev, radiologist: value }))
              }
              placeholder="Select Radiologist"
              showSearch
              allowClear
              optionFilterProp="children"
            >
              {radiologists.map(r => (
                <Option key={r.id} value={r.id}>
                  {r.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <div className="col-lg-4">
          <Form.Item label="Priority">
            <Select
              value={currentDetail.priority || undefined}
              onChange={(value) =>
                setCurrentDetail(prev => ({ ...prev, priority: value }))
              }
              placeholder="Select Priority"
            >
              {priorities.map(p => (
                <Option key={p.id} value={p.id}>
                  {p.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <div className="col-lg-4 d-flex align-items-center">
          <Button
            icon={<PlusOutlined />}
            type="dashed"
            onClick={handleAddRow}
            disabled={radiologistDetails.length >= 4}
          >
            Add Preference
          </Button>
        </div>
      </div>

      {radiologistDetails.length > 0 && (
        <Table
          columns={columns}
          dataSource={radiologistDetails.map((item, index) => ({ key: index, ...item }))}
          pagination={false}
          rowKey="key"
        />
      )}

      <Typography.Text strong>Patient Details</Typography.Text>
      <div className="row mt-3">
        <div className="col-lg-4">
          <Form.Item
            label="Birth Date"
            name="birthDate"
            rules={[{ required: true, message: "Birth date is required" }]}
          >
            <DatePicker onChange={handleDOBChange} style={{ width: "100%" }}  disabledDate={(current) => current && current > dayjs().endOf("day")}  />
          </Form.Item>
          {ageDisplay && <Text>Calculated Age: {ageDisplay}</Text>}
        </div>
        <div className="col-lg-4">
          <Form.Item
            label="Gender"
            name="gender"
            rules={[{ required: true, message: "Gender is required" }]}
          >
            <Select placeholder="Select Gender">
              <Option value="Male">Male</Option>
              <Option value="Female">Female</Option>
              <Option value="Other">Other</Option>
            </Select>
          </Form.Item>
        </div>
        <div className="col-lg-4">
          <Form.Item
            label="Modality"
            name="modality"
            rules={[{ required: true, message: "Modality is required" }]}
          >
            <Select placeholder="Select Modality">
              <Option value="XRAY">XRAY</Option>
              <Option value="CT">CT</Option>
              <Option value="MRI">MRI</Option>
            </Select>
          </Form.Item>
        </div>
        <div className="col-lg-4">
          <Form.Item
            label="Referred By"
            name="reference"
            rules={[{ required: true, message: "Reference is required" }]}
          >
            <Input placeholder="Enter referred by" />
          </Form.Item>
        </div>
        <div className="col-lg-4">
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Description is required" }]}
          >
            <Input.TextArea placeholder="Enter description" autoSize={{ minRows: 2 }} />
          </Form.Item>
        </div>
      </div>

      <Typography.Text strong>Upload Files</Typography.Text>
      <div className="row">
        <div className="col-lg-6">
          <Form.Item label="Upload Documents">
            <Upload
              multiple
              beforeUpload={(file) => {
                setImageFiles(prev => [...prev, file]);
                return false;
              }}
              onRemove={(file) => {
                setImageFiles(prev => prev.filter(f => f.uid !== file.uid));
              }}
              fileList={imageFiles}
            >
              <Button icon={<UploadOutlined />}>Select Files</Button>
            </Upload>
          </Form.Item>
        </div>
        <div className="col-lg-6">
          <Form.Item
            label="Upload Study"
            name="study"
            rules={[{ required: true, message: "Study file is required" }]}
          >
            <Upload
              beforeUpload={(file) => {
                setStudyFile(file);
                return false;
              }}
              onRemove={() => setStudyFile(null)}
              fileList={studyFile ? [studyFile] : []}
            >
              <Button icon={<UploadOutlined />}>Select Study File</Button>
            </Upload>
          </Form.Item>
        </div>
        <div className="col-lg-2 d-flex align-items-center">
          <Form.Item name="emergency" valuePropName="checked">
            <Checkbox>Emergency</Checkbox>
          </Form.Item>
        </div>
      </div>

      <div className="row">
        <div className="col-12 text-right">
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
          <Button
            className="ml-2"
            onClick={() => {
              form.resetFields();
              setRadiologistDetails([]);
              setImageFiles([]);
              setStudyFile(null);
              setAgeDisplay("");
            }}
          >
            Reset
          </Button>
        </div>
      </div>
    </Form>
  );
}

export default UploadStudyModal;
